import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { AlertCircle, ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type PdfPreviewFile = string | File | Blob | { url: string };

interface PdfPreviewProps {
  /** Source for the PDF. URL string, File/Blob, or `{ url }`. */
  file: PdfPreviewFile;
  /** Optional link target for the "Open in new tab" fallback when rendering fails. */
  openInNewTabHref?: string;
  /** Test-id prefix used to namespace `data-testid` attributes. */
  testIdPrefix?: string;
  /** Extra class for the outer container. */
  className?: string;
}

export function PdfPreview({
  file,
  openInNewTabHref,
  testIdPrefix = "pdf-preview",
  className = "",
}: PdfPreviewProps) {
  const containerEl = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  // When the source is a remote URL string, fetching it as a Blob and handing
  // react-pdf a `blob:` URL bypasses CORS failures that occur when the worker
  // tries to fetch the object-storage URL directly. `null` means "not resolved
  // yet"; we fall back to the raw URL only if the blob fetch fails.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobFailed, setBlobFailed] = useState<boolean>(false);

  // Reset page state when the file source changes so callers swapping
  // documents don't get stuck on a stale page number from the previous one.
  const fileKey = typeof file === "string" ? file : (file as any)?.url ?? `${(file as any)?.name ?? ""}-${(file as any)?.size ?? ""}`;
  useEffect(() => {
    setPageNumber(1);
    setNumPages(0);
    setError(null);
  }, [fileKey]);

  // For URL-string sources, attempt a blob fetch up front. On any failure we
  // record `blobFailed` and fall through to letting react-pdf try the raw URL
  // (which then surfaces the existing error state if it too fails).
  const urlSource = typeof file === "string" ? file : null;
  useEffect(() => {
    if (!urlSource) {
      setBlobUrl(null);
      setBlobFailed(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setBlobUrl(null);
    setBlobFailed(false);
    (async () => {
      try {
        const res = await fetch(urlSource, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        if (!cancelled) setBlobFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [urlSource]);

  // Pick the source react-pdf renders: prefer the fetched blob for URL sources,
  // fall back to the raw URL if the blob fetch failed, otherwise use the
  // File/Blob/{url} object passed in directly.
  const resolvedFile: PdfPreviewFile = urlSource
    ? (blobUrl ?? urlSource)
    : file;
  // While a URL source is still being fetched (no blob yet, no failure), hold
  // off rendering react-pdf so it doesn't kick off a CORS-doomed direct fetch.
  const awaitingBlob = !!urlSource && blobUrl === null && !blobFailed;

  // Track container width so each page renders at full width (with a small
  // gutter) — matches the mail-document-review experience.
  useEffect(() => {
    const node = containerEl.current;
    if (!node) return;
    const update = () => setContainerWidth(Math.max(0, node.clientWidth - 16));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const fallbackHref = openInNewTabHref ?? (typeof file === "string" ? file : undefined);

  return (
    <div className={`flex flex-col min-h-0 h-full w-full ${className}`} data-testid={`${testIdPrefix}-root`}>
      <div
        ref={containerEl}
        className="flex-1 min-h-0 overflow-auto p-2 flex justify-center bg-gray-50"
        data-testid={`${testIdPrefix}-container`}
      >
        {error ? (
          <div className="flex flex-col items-center justify-center text-sm text-gray-600 gap-3 p-6">
            <AlertCircle className="h-6 w-6 text-amber-500" />
            <p>Couldn't render PDF preview.</p>
            {error && (
              <p className="text-xs text-gray-400 text-center max-w-xs" data-testid={`${testIdPrefix}-error-detail`}>
                {error}
              </p>
            )}
            {fallbackHref && (
              <Button
                asChild
                variant="default"
                size="sm"
                data-testid={`${testIdPrefix}-open-link`}
              >
                <a href={fallbackHref} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in new tab
                </a>
              </Button>
            )}
          </div>
        ) : awaitingBlob ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading PDF…
          </div>
        ) : (
          <Document
            file={resolvedFile}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setError(null);
            }}
            onLoadError={(err) =>
              // eslint-disable-next-line parse-error/no-raw-error-message-leak -- err is a react-pdf / PDF.js load error (not an apiRequest Error); its message ("Invalid PDF structure", etc.) is already user-readable and carries no "NNN:" status prefix.
              setError(err?.message ?? "Failed to load PDF")
            }
            loading={
              <div className="flex items-center justify-center h-full text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading PDF…
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={containerWidth > 0 ? containerWidth : undefined}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="shadow bg-white"
            />
          </Document>
        )}
      </div>
      {numPages > 0 && !error && (
        <div
          className="shrink-0 border-t border-gray-200 bg-white px-3 py-2 flex items-center justify-center gap-3"
          data-testid={`${testIdPrefix}-pager`}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
            data-testid={`${testIdPrefix}-prev-page`}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-gray-700" data-testid={`${testIdPrefix}-page-counter`}>
            Page {pageNumber} of {numPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages}
            data-testid={`${testIdPrefix}-next-page`}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
